import { useEffect, useMemo, useRef, useState } from "react";
import { PortfolioDataItem } from "@/types/types";
import BrandPillTutorial from "./BrandPillTutorial";
import { Share2 } from "lucide-react";
import {
  getPortfolioBrandPath,
  getPortfolioCategoryPath,
  sharePortfolioPath,
} from "@/lib/portfolioLinks";

type PortfolioMode = "creatives" | "videos" | "websites";

interface CategoryBrowserProps {
  groups: PortfolioDataItem[];
  mode: PortfolioMode;
  onSelect: (group: PortfolioDataItem) => void;
  highlightedCategory?: string | null;
  showTutorial: boolean;
  onTutorialSeen: () => void;
}

interface CategoryGroup {
  title: string;
  categoryDesc: string;
  items: PortfolioDataItem[];
}

const CategoryBrowser: React.FC<CategoryBrowserProps> = ({
  groups,
  mode,
  onSelect,
  highlightedCategory,
  showTutorial,
  onTutorialSeen,
}) => {
  const highlightedRef = useRef<HTMLElement | null>(null);
  const [isTutorialReady, setIsTutorialReady] = useState(false);

  const categories = useMemo(() => {
    const map = new Map<string, CategoryGroup>();

    groups.forEach((group) => {
      if (!map.has(group.title)) {
        map.set(group.title, {
          title: group.title,
          categoryDesc: group.categoryDesc,
          items: [],
        });
      }

      map.get(group.title)?.items.push(group);
    });

    return Array.from(map.values());
  }, [groups]);

  useEffect(() => {
    if (!highlightedCategory) return;

    highlightedRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [highlightedCategory, categories]);

  useEffect(() => {
    setIsTutorialReady(false);

    const timeout = window.setTimeout(
      () => {
        setIsTutorialReady(true);
      },
      highlightedCategory ? 500 : 0,
    );

    return () => window.clearTimeout(timeout);
  }, [highlightedCategory, mode]);

  const handleBrandSelect = (item: PortfolioDataItem) => {
    if (showTutorial) onTutorialSeen();
    onSelect(item);
  };

  const getItemLabel = () => {
    if (mode === "creatives") return "visuals";
    if (mode === "videos") return "videos";
    return "websites";
  };

  const getCategoryType = (): PortfolioDataItem["type"] => {
    if (mode === "creatives") return "image";
    if (mode === "videos") return "video";
    return "website";
  };

  return (
    <>
      <div className="space-y-6 lg:space-y-8">
        {categories.map((category) => {
          const isHighlighted = highlightedCategory === category.title;

          return (
            <section
              key={category.title}
              ref={(element) => {
                if (isHighlighted) highlightedRef.current = element;
              }}
              className={`rounded-lg border bg-[#f5f5f5]/5 p-3 lg:p-6 ${
                isHighlighted
                  ? "border-[#c3f620]/80 shadow-[0_0_30px_rgba(195,246,32,0.16)]"
                  : "border-[#f5f5f5]/10"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h2 className="font-orbitron uppercase tracking-[1px] text-xl lg:text-3xl">
                    {category.title}
                  </h2>
                  <p className="mt-2 max-w-4xl font-poppins text-sm leading-6 text-[#f5f5f5]/65">
                    {category.categoryDesc}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    sharePortfolioPath(
                      `${category.title} ${mode}`,
                      getPortfolioCategoryPath(
                        getCategoryType(),
                        category.title,
                      ),
                    )
                  }
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[#00a878]/40 text-[#f5f5f5]/75 outline-none ring-[#00a878] transition hover:bg-[#00a878] hover:text-[#f5f5f5] focus-visible:ring-2"
                  aria-label={`Share ${category.title}`}
                >
                  <Share2 className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-4 flex w-fit items-center overflow-hidden rounded-full border border-[#00a878]/40 font-poppins text-xs text-[#c3f620]">
                <span className="px-3 py-1">
                    {category.items.reduce(
                      (total, item) => total + item.media.length,
                      0,
                    )}{" "}
                    {getItemLabel()}
                </span>
              </div>

              <div className="mt-5 flex flex-wrap gap-3">
                {category.items.map((item, index) => (
                  <div
                    key={`${item.title}-${item.brand}`}
                    data-brand-pill="true"
                    data-tutorial-target={
                      (isHighlighted || !highlightedCategory) && index === 0
                        ? "true"
                        : undefined
                    }
                    className="flex items-center overflow-hidden rounded-full border border-[#00a878]/40 bg-[#0d1821]"
                  >
                    <button
                      type="button"
                      onClick={() => handleBrandSelect(item)}
                      className="px-4 py-2 font-poppins text-sm font-medium text-[#f5f5f5] outline-none ring-[#00a878] transition hover:bg-[#00a878] focus-visible:ring-2"
                    >
                      {item.brand}
                      <span className="ml-2 text-[#c3f620]">
                        {item.media.length}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        sharePortfolioPath(
                          `${item.brand} ${mode}`,
                          getPortfolioBrandPath(item),
                        )
                      }
                      className="grid pr-1 h-9 w-9 place-items-center border-l border-[#00a878]/40 text-[#f5f5f5]/75 outline-none ring-[#00a878] transition hover:bg-[#00a878] hover:text-[#f5f5f5] focus-visible:ring-2"
                      aria-label={`Share ${item.brand}`}
                    >
                      <Share2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {showTutorial && isTutorialReady && categories.length > 0 && (
        <BrandPillTutorial
          onSkip={onTutorialSeen}
          targetSelector="[data-tutorial-target='true']"
        />
      )}
    </>
  );
};

export default CategoryBrowser;
